import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  fr: {
    // Navigation
    dashboard: 'Tableau de bord',
    products: 'Produits',
    stock: 'Stock',
    suppliers: 'Fournisseurs',
    customers: 'Clients',
    orders: 'Commandes',
    sales: 'Ventes',
    purchases: 'Achats',
    warehouses: 'Entrepôts',
    returns: 'Retours',
  documents: 'Documents',
  
    reports: 'Rapports',
    advancedAnalytics: 'Analyses avancées',
    employees: 'Employés',
    settings: 'Paramètres',
    logout: 'Déconnexion',
    
    // Common
    add: 'Ajouter',
    edit: 'Modifier',
    delete: 'Supprimer',
    save: 'Enregistrer',
    cancel: 'Annuler',
    search: 'Rechercher',
    filter: 'Filtrer',
    export: 'Exporter',
    import: 'Importer',
    loading: 'Chargement...',
    
    // Auth
    login: 'Se connecter',
    email: 'Email',
    password: 'Mot de passe',
    rememberMe: 'Se souvenir de moi',
    forgotPassword: 'Mot de passe oublié ?',
    
    // Products
    productName: 'Nom du produit',
    productCode: 'Code produit',
    productCategory: 'Catégorie',
    price: 'Prix',
    cost: 'Coût',
    quantity: 'Quantité',
    unit: 'Unité',
    
    // Stock
    stockMovements: 'Mouvements de stock',
    stockAdjustments: 'Ajustements de stock',
    stockTransfers: 'Transferts de stock',
    stockAlerts: 'Alertes de stock',
    stockStatus: 'État des stocks',
    minimumStock: 'Stock minimum',
    expirationDate: 'Date d\'expiration',
    batch: 'Lot',
    serial: 'Numéro de série',
    
    // Suppliers & Customers
    name: 'Nom',
    phone: 'Téléphone',
    email: 'Email',
    address: 'Adresse',
    status: 'Statut',
    active: 'Actif',
    inactive: 'Inactif',
    
    // Orders
    salesOrders: 'Commandes clients',
    purchaseOrders: 'Commandes fournisseurs',
    orderNumber: 'Numéro de commande',
    orderDate: 'Date de commande',
    deliveryDate: 'Date de livraison',
    orderStatus: 'Statut de la commande',
    
    // Payments
    amount: 'Montant',
    paymentMethod: 'Méthode de paiement',
    paymentDate: 'Date de paiement',
    invoice: 'Facture',
    
    // Dashboard
    totalProducts: 'Total des produits',
    lowStock: 'Stock faible',
    outOfStock: 'Rupture de stock',
    salesOverview: 'Aperçu des ventes',
  },
  ar: {
    // Navigation
    dashboard: 'لوحة القيادة',
    products: 'المنتجات',
    stock: 'المخزون',
    suppliers: 'الموردين',
    customers: 'العملاء',
    orders: 'الطلبات',
    sales: 'المبيعات',
    purchases: 'المشتريات',
    warehouses: 'المستودعات',
    returns: 'المرتجعات',
  documents: 'المستندات',
  
    reports: 'التقارير',
    advancedAnalytics: 'التحليلات المتقدمة',
    employees: 'الموظفين',
    settings: 'الإعدادات',
    logout: 'تسجيل الخروج',
    
    // Common
    add: 'إضافة',
    edit: 'تعديل',
    delete: 'حذف',
    save: 'حفظ',
    cancel: 'إلغاء',
    search: 'بحث',
    filter: 'تصفية',
    export: 'تصدير',
    import: 'استيراد',
    loading: 'جاري التحميل...',
    
    // Auth
    login: 'تسجيل الدخول',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    rememberMe: 'تذكرني',
    forgotPassword: 'نسيت كلمة المرور؟',
    
    // Products
    productName: 'اسم المنتج',
    productCode: 'رمز المنتج',
    productCategory: 'فئة المنتج',
    price: 'السعر',
    cost: 'التكلفة',
    quantity: 'الكمية',
    unit: 'الوحدة',
    
    // Stock
    stockMovements: 'حركات المخزون',
    stockAdjustments: 'تعديلات المخزون',
    stockTransfers: 'تحويلات المخزون',
    stockAlerts: 'تنبيهات المخزون',
    stockStatus: 'حالة المخزون',
    minimumStock: 'الحد الأدنى للمخزون',
    expirationDate: 'تاريخ انتهاء الصلاحية',
    batch: 'الدفعة',
    serial: 'الرقم التسلسلي',
    
    // Suppliers & Customers
    name: 'الاسم',
    phone: 'الهاتف',
    email: 'البريد الإلكتروني',
    address: 'العنوان',
    status: 'الحالة',
    active: 'نشط',
    inactive: 'غير نشط',
    
    // Orders
    salesOrders: 'طلبات المبيعات',
    purchaseOrders: 'طلبات الشراء',
    orderNumber: 'رقم الطلب',
    orderDate: 'تاريخ الطلب',
    deliveryDate: 'تاريخ التسليم',
    orderStatus: 'حالة الطلب',
    
    // Payments
    amount: 'المبلغ',
    paymentMethod: 'طريقة الدفع',
    paymentDate: 'تاريخ الدفع',
    invoice: 'الفاتورة',
    
    // Dashboard
    totalProducts: 'إجمالي المنتجات',
    lowStock: 'مخزون منخفض',
    outOfStock: 'نفاد المخزون',
    salesOverview: 'نظرة عامة على المبيعات',
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('fr');
  const [isLoading, setIsLoading] = useState(true);

  // Load language preference from API
  useEffect(() => {
    const fetchLanguagePreference = async () => {
      try {
        const response = await axios.get('/settings/language');
        if (response.data && response.data.language) {
          setLanguage(response.data.language);
        }
      } catch (error) {
        console.error('Error fetching language preference:', error);
        // Default to French if API call fails
        setLanguage('fr');
      } finally {
        setIsLoading(false);
      }
    };

    fetchLanguagePreference();
  }, []);

  // Update language preference through API
  useEffect(() => {
    if (!isLoading) {
      const saveLanguagePreference = async () => {
        try {
          await axios.post('/settings/language', { language });
        } catch (error) {
          console.error('Error saving language preference:', error);
        }
      };

      saveLanguagePreference();
    }

    // Update document attributes
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language, isLoading]);

  const t = (key) => {
    return translations[language][key] || key;
  };

  const changeLanguage = (newLanguage) => {
    if (translations[newLanguage]) {
      setLanguage(newLanguage);
    }
  };

  const value = {
    language,
    changeLanguage,
    setLanguage, // Expose setter for legacy components
    t,
    isRTL: language === 'ar',
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}; 